class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');
    this.container = this.closest('[data-predictive-search-container]');
    this.closeButton = this.querySelector('[data-predictive-search-close]')
      || this.closest('.collapse')?.querySelector('[data-predictive-search-close]')
      || this.container?.querySelector('[data-predictive-search-close]');
    this.collapseElement = this.closest('.collapse') || this.container?.querySelector('.collapse');
    this.handleCollapseShown = this.showCloseButton.bind(this);
    this.handleCollapseHidden = this.hideCloseButton.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);

    if (!(this.input instanceof HTMLInputElement) || !(this.predictiveSearchResults instanceof HTMLElement)) {
      return;
    }

    this.input.addEventListener('input', this.debounce(() => {
      this.onChange();
    }, 300));

    if (this.closeButton instanceof HTMLButtonElement) {
      this.updateCloseButtonVisibility();
      this.closeButton.addEventListener('click', () => {
        this.close();
      });
    }

    if (this.collapseElement instanceof HTMLElement) {
      this.collapseElement.addEventListener('shown.bs.collapse', this.handleCollapseShown);
      this.collapseElement.addEventListener('hide.bs.collapse', this.handleCollapseHidden);

      if (!window.bootstrap?.Collapse) {
        this.createCollapseObserver();
      }
    }
  }

  onChange() {
    if (!(this.input instanceof HTMLInputElement)) {
      return;
    }

    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    if (!(this.predictiveSearchResults instanceof HTMLElement)) {
      return;
    }

    fetch(`/search/suggest?q=${searchTerm}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(String(response.status));
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.open();
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  open() {
    if (!(this.predictiveSearchResults instanceof HTMLElement)) {
      return;
    }

    this.predictiveSearchResults.style.display = 'block';
    document.addEventListener('click', this.handleDocumentClick);
  }

  close() {
    if (!(this.predictiveSearchResults instanceof HTMLElement)) {
      return;
    }

    this.predictiveSearchResults.style.display = 'none';
    document.removeEventListener('click', this.handleDocumentClick);

    const collapseElement = this.closest('.collapse');
    if (collapseElement?.classList.contains('show') && window.bootstrap?.Collapse) {
      window.bootstrap.Collapse.getOrCreateInstance(collapseElement, { toggle: false }).hide();
    } else if (collapseElement?.classList.contains('show')) {
      collapseElement.classList.remove('show');
      collapseElement.setAttribute('aria-expanded', 'false');
      this.hideCloseButton();
    }
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  showCloseButton() {
    if (this.closeButton instanceof HTMLButtonElement) {
      this.closeButton.classList.remove('d-none');
    }
  }

  hideCloseButton() {
    if (this.closeButton instanceof HTMLButtonElement) {
      this.closeButton.classList.add('d-none');
    }
  }

  updateCloseButtonVisibility() {
    if (!(this.collapseElement instanceof HTMLElement)) {
      return;
    }

    if (this.collapseElement.classList.contains('show')) {
      this.showCloseButton();
    } else {
      this.hideCloseButton();
    }
  }

  createCollapseObserver() {
    if (!(this.collapseElement instanceof HTMLElement)) {
      return;
    }

    this.collapseObserver = new MutationObserver(() => {
      this.updateCloseButtonVisibility();
    });

    this.collapseObserver.observe(this.collapseElement, { attributes: true, attributeFilter: ['class', 'aria-expanded'] });
    this.updateCloseButtonVisibility();
  }
}

export default PredictiveSearch;